import { useState, useEffect, useCallback } from "react";

const POLL_INTERVAL = 30_000; // 30 seconds
const BOARD_URL = "/api/board";

function formatTime(timestamp) {
  if (!timestamp) return "";
  const d = new Date(timestamp);
  return d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
}

function statusClass(status) {
  if (status === "needs_attention") return "needs-attention";
  return status; // "checked", "flagged", "pending"
}

const COLUMN_ORDER = ["checked", "flagged", "needs_attention", "pending"];
const COLUMN_LABELS = {
  checked: "Checked",
  flagged: "Flagged",
  needs_attention: "Needs Attention",
  pending: "Pending",
};

export default function WhiteboardPanel() {
  const [board, setBoard] = useState(null);
  const [error, setError] = useState(null);
  const [lastFetch, setLastFetch] = useState(null);

  const fetchBoard = useCallback(async () => {
    try {
      const res = await fetch(BOARD_URL);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setBoard(data);
      setError(null);
      setLastFetch(new Date());
    } catch (err) {
      console.error("[whiteboard] Fetch failed:", err);
      setError(err.message);
    }
  }, []);

  useEffect(() => {
    fetchBoard();
    const id = setInterval(fetchBoard, POLL_INTERVAL);
    return () => clearInterval(id);
  }, [fetchBoard]);

  // ── Loading state ──
  if (!board && !error) {
    return (
      <section className="panel section whiteboard-panel" id="grow-room-audit-whiteboard">
        <div className="section-head">
          <div>
            <div className="eyebrow">WhatsApp Audit Whiteboard</div>
            <h2>Loading board…</h2>
          </div>
        </div>
      </section>
    );
  }

  // ── Error state (no data yet) ──
  if (!board && error) {
    return (
      <section className="panel section whiteboard-panel" id="grow-room-audit-whiteboard">
        <div className="section-head">
          <div>
            <div className="eyebrow">WhatsApp Audit Whiteboard</div>
            <h2>Could not load board</h2>
            <p>{error}</p>
          </div>
          <div className="btn-row">
            <button className="pill pill-soft" onClick={fetchBoard}>Retry</button>
          </div>
        </div>
      </section>
    );
  }

  const { columns, summary, activity } = board;

  return (
    <section className="panel section whiteboard-panel" id="grow-room-audit-whiteboard">
      <div className="section-head">
        <div>
          <div className="eyebrow">WhatsApp Audit Whiteboard</div>
          <h2>Live room status with visible message history</h2>
          <p>
            Four status columns, daily progress strip, and live activity
            rail — powered by the WhatsApp ingest API.
          </p>
        </div>
        <div className="btn-row">
          <span className="pill pill-soft">
            {error ? "Connection lost" : "Office Bot Online"}
          </span>
          <span className="pill pill-good">
            {summary.total} rooms rostered
          </span>
        </div>
      </div>

      <div className="whiteboard-stack">
        {/* ── Progress strip ── */}
        <div className="progress-strip">
          <div className="progress-head">
            <div>
              <span>Daily Audit Progress</span>
              <strong>
                {summary.audited} / {summary.total} audited today
              </strong>
            </div>
            <div className="progress-meta">
              <span className="pill pill-good">{summary.checked} checked</span>
              <span className="pill pill-warn">{summary.flagged} flagged</span>
              <span className="pill pill-bad">{summary.needs_attention} needs attention</span>
              <span className="pill pill-soft">{summary.pending} pending</span>
            </div>
          </div>
          <div className="progress-bar" aria-label="Daily audit completion">
            <div
              className="progress-fill"
              style={{
                width: summary.total > 0
                  ? `${(summary.audited / summary.total) * 100}%`
                  : "0%",
              }}
            />
          </div>
        </div>

        {/* ── Board + Activity rail ── */}
        <div className="whiteboard-layout">
          <div className="audit-columns">
            {COLUMN_ORDER.map((col) => (
              <div className="audit-column" data-audit-column={col} key={col}>
                <div className="audit-column-head">
                  <strong>{COLUMN_LABELS[col]}</strong>
                  <span>{(columns[col] || []).length} rooms</span>
                </div>
                {(columns[col] || []).map((room) => (
                  <article
                    className={`audit-room-card ${statusClass(room.status)}`}
                    key={room.code}
                  >
                    <div className="audit-room-head">
                      <div>
                        <strong>{room.code}{room.name ? ` · ${room.name}` : ""}</strong>
                        <span>
                          {room.posted_by
                            ? `Latest by ${room.posted_by}`
                            : "No audit posted yet"}
                        </span>
                      </div>
                      <div className="audit-stamp">
                        {room.timestamp ? (
                          <>
                            {formatTime(room.timestamp)}
                            <br />
                            {room.status === "needs_attention" ? "attention" : room.status}
                          </>
                        ) : (
                          "Waiting"
                        )}
                      </div>
                    </div>
                    <div className="audit-note">
                      {room.message
                        ? `Original message: "${room.message}"`
                        : "No accepted WhatsApp audit has been received for this room today."}
                    </div>
                    {room.events && room.events.length > 0 && (
                      <div className="event-stack">
                        {room.events.map((evt, i) => (
                          <div className="event-chip" key={i}>
                            <strong>{formatTime(evt.timestamp)}</strong>{" "}
                            {evt.posted_by} — {evt.status}
                            {evt.note ? `: ${evt.note}` : ""}
                          </div>
                        ))}
                      </div>
                    )}
                  </article>
                ))}
              </div>
            ))}
          </div>

          <aside className="activity-rail" aria-label="Live activity rail">
            <div>
              <div className="eyebrow">Live Activity Rail</div>
              <h4>Incoming WhatsApp audit feed</h4>
              <p>Newest accepted events land here first, then move the room card in the board.</p>
            </div>
            <div className="activity-list">
              {activity.length === 0 && (
                <div className="workspace-note">No activity yet today.</div>
              )}
              {activity.map((evt, i) => (
                <article className={`activity-item ${evt.status ? statusClass(evt.status) : ''}`} key={i}>
                  <strong>
                    {formatTime(evt.timestamp)} · {evt.posted_by || 'Unknown'}
                    {evt.room_code ? ` · ${evt.room_code}` : ''}
                  </strong>
                  <span>{evt.message}</span>
                </article>
              ))}
            </div>
            {lastFetch && (
              <div className="workspace-note">
                Last refreshed {formatTime(lastFetch)} · polling every 30s
              </div>
            )}
          </aside>
        </div>
      </div>
    </section>
  );
}
